/**
 * InvoiceForm.jsx — Create invoice from a job
 *
 * Features:
 * - Invoice type selection: Deposit, Balance, Full
 * - Auto-calculated amounts from job revenue + settings.depositPct
 * - Auto-filled client info from job
 * - Invoice number auto-generated from settings prefix + counter
 * - Due date calculated from payment terms
 * - Notes field
 * - Creates invoice via useCreateInvoice mutation
 */

import { useState, useMemo, useEffect } from 'react';
import { uid, fmtC } from '../../lib/utils';
import { defaultDueDate, termsLabel } from '../../lib/invoiceDefaults';
import { useCreateInvoice, useInvoicesByJob } from '../../hooks/useInvoices';
import { useSettings } from '../../hooks/useSettings';
import { useInvoices } from '../../hooks/useInvoices';
import { useXeroStatus, useSyncInvoiceToXero } from '../../hooks/useXero';
import { useToast } from '../layout/Toast';

export default function InvoiceForm({ job, onSave, onCancel, prefillType }) {
  const { toast } = useToast();
  const { data: settings } = useSettings();
  const { data: allInvoices = [] } = useInvoices();
  const { data: jobInvoices = [] } = useInvoicesByJob(job?.id);
  const createMutation = useCreateInvoice();
  const { data: xeroStatus } = useXeroStatus();
  const syncMutation = useSyncInvoiceToXero();
  const xeroConnected = xeroStatus?.connected === true;

  // ── Compute existing invoice state for this job ──────────────────────────
  const existingDeposit = jobInvoices.find(
    (inv) => inv.invoiceType === 'deposit' && inv.status !== 'void'
  );
  const existingBalance = jobInvoices.find(
    (inv) => inv.invoiceType === 'balance' && inv.status !== 'void'
  );

  // ── Invoice number generation (PREFIX-YYMMDD-SEQ, daily reset) ───────────
  const nextNumber = useMemo(() => {
    const prefix = settings?.data?.invoicePrefix || 'INV';
    const startNum = parseInt(settings?.data?.invoiceStart || '1', 10) || 1;
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const dateStr = `${yy}${mm}${dd}`;

    // Find the highest existing number for today
    const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = new RegExp(`^${escaped}-${dateStr}-(\\d+)$`);
    let maxNum = startNum - 1;
    allInvoices.forEach((inv) => {
      const match = (inv.number || '').match(pattern);
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > maxNum) maxNum = num;
      }
    });

    return `${prefix}-${dateStr}-${String(maxNum + 1).padStart(3, '0')}`;
  }, [settings, allInvoices]);

  // ── Form state ───────────────────────────────────────────────────────────
  const depositPct = parseFloat(settings?.data?.depositPct || 50);
  const jobRevenue = parseFloat(job?.revenue || 0);

  const [invoiceType, setInvoiceType] = useState(() => {
    if (prefillType === 'extra') return 'extra';
    if (prefillType === 'balance' && !existingBalance) return 'balance';
    if (prefillType === 'deposit' && !existingDeposit) return 'deposit';
    if (!existingDeposit) return 'deposit';
    if (!existingBalance) return 'balance';
    return 'full';
  });

  const [notes, setNotes] = useState('');
  const [extraAmount, setExtraAmount] = useState(''); // free amount for 'extra' charges
  const [dueDate, setDueDate] = useState(() => defaultDueDate(invoiceType, settings));

  // When invoice type changes, update due date: deposits due immediately, others
  // use final terms. Keyed on the derived string, not the settings object.
  const typeDueDate = defaultDueDate(invoiceType, settings);
  useEffect(() => {
    setDueDate(typeDueDate);
  }, [typeDueDate]);

  // ── Amount calculation ───────────────────────────────────────────────────
  const amount = useMemo(() => {
    if (invoiceType === 'extra') {
      // Extra work charge — free amount, ON TOP of revenue (not derived from it)
      return Math.round((parseFloat(extraAmount) || 0) * 100) / 100;
    }
    if (invoiceType === 'deposit') {
      return Math.round(jobRevenue * (depositPct / 100) * 100) / 100;
    }
    if (invoiceType === 'balance') {
      const depositAmt = (existingDeposit && existingDeposit.status === 'paid')
        ? parseFloat(existingDeposit.amount || 0)
        : (existingDeposit ? 0 : Math.round(jobRevenue * (depositPct / 100) * 100) / 100);
      return Math.round((jobRevenue - depositAmt) * 100) / 100;
    }
    return jobRevenue;
  }, [invoiceType, jobRevenue, depositPct, existingDeposit, extraAmount]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const typeOptions = [
    {
      value: 'deposit',
      label: `Deposit (${depositPct}%)`,
      disabled: !!existingDeposit,
      hint: existingDeposit ? 'Already created' : null,
    },
    {
      value: 'balance',
      label: 'Balance',
      disabled: !existingDeposit || !!existingBalance,
      hint: !existingDeposit
        ? 'Create deposit first'
        : existingBalance
          ? 'Already created'
          : null,
    },
    {
      value: 'full',
      label: 'Full Amount',
      disabled: false,
      hint: null,
    },
    {
      value: 'extra',
      label: 'Extra Charge',
      disabled: false,
      hint: 'Extra work — free amount',
    },
  ];

  // ── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isNaN(amount) || amount <= 0) {
      toast('Invoice amount must be a valid number greater than zero', 'error');
      return;
    }

    const invoice = {
      id: uid(),
      number: nextNumber,
      jobId: job.id,
      client: job.clientName || job.client || '',
      invoiceType,
      amount,
      paidAmount: 0,
      status: 'unpaid',
      terms: termsLabel(invoiceType, settings),
      dueDate,
      notes,
      payments: [],
    };

    try {
      await createMutation.mutateAsync(invoice);
      toast(`Invoice ${nextNumber} created — ${fmtC(amount)}`);
      if (xeroConnected) syncMutation.mutate(invoice.id);
      onSave?.();
    } catch (err) {
      toast(`Failed to create invoice: ${err.message}`, 'error');
    }
  };

  if (!job) return null;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-heading text-text-primary">Create Invoice</h3>
        <span className="text-2xs text-text-dim font-mono">{nextNumber}</span>
      </div>

      {/* Job summary */}
      <div className="bg-surface border border-border rounded p-3 text-xs space-y-1">
        <div className="flex justify-between">
          <span className="text-text-dim">Job</span>
          <span className="text-text-primary font-semibold">{job.title}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-text-dim">Client</span>
          <span className="text-text-primary">{job.clientName || job.client || '—'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-text-dim">Net Revenue</span>
          <span className="text-accent font-semibold">{fmtC(jobRevenue)}</span>
        </div>
      </div>

      {/* Invoice type selection */}
      <div>
        <label className="block text-2xs font-semibold text-text-dim mb-2 uppercase">Invoice Type</label>
        <div className="grid grid-cols-2 gap-2">
          {typeOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={opt.disabled}
              onClick={() => !opt.disabled && setInvoiceType(opt.value)}
              className={`p-2 rounded border text-xs text-center transition-colors ${
                invoiceType === opt.value
                  ? 'bg-accent/10 border-accent text-accent font-semibold'
                  : opt.disabled
                    ? 'bg-surface border-border text-text-dim opacity-50 cursor-not-allowed'
                    : 'bg-surface border-border text-text-primary hover:border-accent/50'
              }`}
            >
              <div>{opt.label}</div>
              {opt.hint && (
                <div className="text-2xs text-text-dim mt-0.5">{opt.hint}</div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Amount — free entry for extra charges, computed otherwise */}
      {invoiceType === 'extra' ? (
        <div>
          <label className="block text-2xs font-semibold text-text-dim mb-2 uppercase">Extra Charge Amount</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim">$</span>
            <input
              type="number" min="0" step="0.01"
              value={extraAmount}
              onChange={(e) => setExtraAmount(e.target.value)}
              placeholder="0.00"
              className="w-full pl-8 pr-3 py-2.5 bg-surface border border-border rounded text-text-primary text-lg font-heading focus:outline-none focus:border-accent"
            />
          </div>
          <div className="text-2xs text-text-dim mt-1">Billed on top of revenue — not counted toward the project balance.</div>
        </div>
      ) : (
      <div className="bg-accent/5 border border-accent/20 rounded p-3 text-center">
        <div className="text-2xs text-text-dim mb-1">Invoice Amount</div>
        <div className="text-2xl font-heading text-accent">{fmtC(amount)}</div>
        {invoiceType === 'deposit' && (
          <div className="text-2xs text-text-dim mt-1">
            {depositPct}% of {fmtC(jobRevenue)}
          </div>
        )}
        {invoiceType === 'balance' && existingDeposit && (
          <div className="text-2xs text-text-dim mt-1">
            {fmtC(jobRevenue)} − {fmtC(existingDeposit.amount)} deposit
          </div>
        )}
      </div>
      )}

      {/* Due date */}
      <div>
        <label className="block text-2xs font-semibold text-text-dim mb-1 uppercase">Due Date</label>
        <input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="w-full px-3 py-2 bg-surface border border-border rounded text-sm text-text-primary focus:outline-none focus:border-accent transition-colors"
        />
      </div>

      {/* Notes */}
      <div>
        <label className="block text-2xs font-semibold text-text-dim mb-1 uppercase">Notes (optional)</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g., Payment via check..."
          maxLength={500}
          rows={2}
          className="w-full px-3 py-2 bg-surface border border-border rounded text-sm text-text-primary placeholder-text-dim focus:outline-none focus:border-accent transition-colors resize-none"
        />
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={createMutation.isPending || amount <= 0}
          className="flex-1 px-3 py-2 bg-accent text-bg text-xs font-semibold rounded hover:bg-accent-hover transition-colors disabled:opacity-50"
        >
          {createMutation.isPending ? 'Creating...' : `Create ${invoiceType} Invoice`}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-2 bg-surface border border-border text-text-primary text-xs font-semibold rounded hover:bg-surface-2 transition-colors"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
